import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import React from "react";
import ColorPicker from "../../../../../../components/ColorPicker/ColorPicker";
import {
  AttributeKey,
  PageUid,
  useCustomisation,
} from "../../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { useTranslation } from "../../../../../../context/TranslationContext";
import { updateSectionDesign } from "../../../../../../services/api/apiClient";
import { brand, gray } from "../../../../../../theme/themePrimitives";

interface ButtonsCustomisationProps {
  pageId?: PageUid;
}

export const ButtonsCustomisation: React.FC<ButtonsCustomisationProps> = ({
  pageId = "GPC",
}) => {
  const { updateCustomisationSection, getCustomisationValue } =
    useCustomisation();
  const { portals } = usePortal();
  const { translations } = useTranslation();
  const buttonsTranslations = translations.translations["portal-design"].gpc;

  const btnPrimaryBgColorKey = `${pageId.toLowerCase()}BtnPrimaryBgColor`;
  const btnPrimaryTextColorKey = `${pageId.toLowerCase()}BtnPrimaryTextColor`;
  const btnSecondaryBgColorKey = `${pageId.toLowerCase()}BtnSecondaryBgColor`;
  const btnSecondaryTextColorKey = `${pageId.toLowerCase()}BtnSecondaryTextColor`;
  const linkColorKey = `${pageId.toLowerCase()}LinkColor`;

  const handleAttributeChange = (
    attributeKey: string,
    newAttributeValue?: string
  ) => {
    if (!newAttributeValue) return;

    updateCustomisationSection(pageId, "buttonsSettings", {
      [attributeKey]: newAttributeValue,
    });

    updateSectionDesign(
      portals.selectedPortal?.portalUid!,
      pageId,
      "buttonsSettings",
      {
        attributeKey,
        attributeValue: newAttributeValue,
      }
    );
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          {buttonsTranslations?.buttonTitle}
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          {buttonsTranslations?.primaryButtonTitle}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Box>
              <Typography>Button background Color</Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "buttonsSettings",
                  btnPrimaryBgColorKey as AttributeKey
                )}
                onChange={(color) =>
                  handleAttributeChange(btnPrimaryBgColorKey, color)
                }
              />
            </Box>
            <Box mt={2}>
              <Typography>Button text Color</Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "buttonsSettings",
                  btnPrimaryTextColorKey as AttributeKey
                )}
                onChange={(color) =>
                  handleAttributeChange(btnPrimaryTextColorKey, color)
                }
              />
            </Box>
          </Box>
          <Box
            sx={{
              backgroundColor: gray[200],
              borderRadius: "8px",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              disabled
              sx={{
                backgroundColor: "black !important",
                color: "white !important",
                borderRadius: "12px",
                padding: "10px 16px",
              }}
            >
              Button CTA
            </Button>
          </Box>
        </Box>
      </Stack>
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          {buttonsTranslations?.secondaryButtonTitle}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Box>
              <Typography>Button background Color</Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "buttonsSettings",
                  btnSecondaryBgColorKey as AttributeKey
                )}
                onChange={(color) =>
                  handleAttributeChange(btnSecondaryBgColorKey, color)
                }
              />
            </Box>
            <Box mt={2}>
              <Typography>Button text Color</Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "buttonsSettings",
                  btnSecondaryTextColorKey as AttributeKey
                )}
                onChange={(color) =>
                  handleAttributeChange(btnSecondaryTextColorKey, color)
                }
              />
            </Box>
          </Box>
          <Box
            sx={{
              backgroundColor: gray[200],
              borderRadius: "8px",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              disabled
              sx={{
                backgroundColor: "white !important",
                color: "black !important",
                borderRadius: "12px",
                padding: "10px 16px",
              }}
            >
              Secondary button
            </Button>
          </Box>
        </Box>
      </Stack>
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          {buttonsTranslations?.linkTitle}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography>Link text Color</Typography>
            <ColorPicker
              value={getCustomisationValue(
                pageId,
                "buttonsSettings",
                linkColorKey as AttributeKey
              )}
              onChange={(color) => handleAttributeChange(linkColorKey, color)}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: gray[200],
              borderRadius: "8px",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography fontWeight={600} color={brand[700]}>
              Link
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
